import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { UseFormReturn } from "react-hook-form";
import type { RateLimitDefaultsSchema } from "../../hooks/useRateLimitForm";
import type { RateLimitDefaults } from "../../types/rate-limit.types";
import { RATE_LIMIT_FEATURES } from "../../constants/rateLimits";

interface RateLimitDefaultsFormProps {
  form: UseFormReturn<RateLimitDefaultsSchema>;
  savedValues?: RateLimitDefaults;
  disabled?: boolean;
}

interface LimitCardProps extends RateLimitDefaultsFormProps {
  name: keyof RateLimitDefaultsSchema;
}

function LimitCard({ form, name, savedValues, disabled }: LimitCardProps) {
  const feature = RATE_LIMIT_FEATURES[name];
  const saved = savedValues?.[name];
  const current = form.watch(name);
  const isChanged = saved !== undefined && current !== saved;

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem
          className={cn(
            "bg-black-950 flex flex-col gap-4 rounded-lg p-5 ring-1 transition-colors duration-200",
            isChanged ? "ring-brand-600/50" : "ring-transparent",
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <span
                className={cn(
                  "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                  feature.accent,
                )}
              >
                <i className={cn(feature.icon, "text-[16px]")} />
              </span>
              <div className="space-y-1">
                <div className="text-sm leading-none font-bold -tracking-wide text-white">
                  {feature.title}
                </div>
                <div className="text-black-500 text-xs leading-none">
                  {feature.scope}
                </div>
              </div>
            </div>
            {isChanged && (
              <span className="text-brand-600 bg-brand-600/10 rounded-full px-2 py-1 text-[10px] leading-none font-medium">
                Edited
              </span>
            )}
          </div>

          <div className="relative">
            <FormControl>
              <Input
                {...field}
                value={field.value ?? ""}
                onChange={(event) =>
                  field.onChange(
                    event.target.value === ""
                      ? undefined
                      : event.target.valueAsNumber,
                  )
                }
                type="number"
                min={1}
                step={1}
                placeholder={String(feature.fallback)}
                disabled={disabled}
                variant="filled"
                className="placeholder:text-black-500 bg-admin-input h-[64px] w-full [appearance:textfield] pr-28 text-3xl leading-none font-bold -tracking-wide text-white focus:ring-1 active:ring-1 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
            </FormControl>
            <span className="text-black-500 pointer-events-none absolute top-1/2 right-5 -translate-y-1/2 text-xs leading-none font-medium tracking-normal uppercase">
              {feature.unit}
            </span>
          </div>

          <FormMessage className="text-xs !text-red-600" />

          <div className="border-admin-input flex items-center justify-between border-t pt-3">
            <span className="text-black-500 text-[10px] leading-none font-medium tracking-normal uppercase">
              In effect
            </span>
            <span className="text-black-300 flex items-center gap-1.5 text-xs leading-none font-medium">
              {saved ?? "—"}
              {isChanged && typeof current === "number" && (
                <>
                  <i className="ri-arrow-right-line text-black-500 text-[12px]" />
                  <span className="text-white">{current}</span>
                </>
              )}
            </span>
          </div>
        </FormItem>
      )}
    />
  );
}

export function RateLimitDefaultsForm({
  form,
  savedValues,
  disabled,
}: RateLimitDefaultsFormProps) {
  return (
    <Form {...form}>
      <form className="grid grid-cols-1 gap-3 p-0.5 md:grid-cols-2 lg:grid-cols-3">
        <LimitCard
          form={form}
          name="keyword_monitoring_calls_per_min"
          savedValues={savedValues}
          disabled={disabled}
        />
        <LimitCard
          form={form}
          name="influencer_monitoring_requests_per_min"
          savedValues={savedValues}
          disabled={disabled}
        />
        <LimitCard
          form={form}
          name="source_monitoring_calls_per_min"
          savedValues={savedValues}
          disabled={disabled}
        />
      </form>
    </Form>
  );
}
